import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { ApiService } from '../../services/api/api.service';
import { ContextService } from '../../services/context/context.service';
import { DomainService } from '../../services/domain/domain.service';

/** Endpoint SAV décrit dans le swagger fourni. */
const SAV_ENDPOINT = 'https://dash.leads.narratheque.io/api/sav-intake.php';

@Component({
    selector: 'app-help',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, ButtonModule, InputTextModule, ToastModule],
    templateUrl: './help.component.html',
    styleUrl: './help.component.scss',
})
export class HelpComponent implements OnInit, OnDestroy {
    /** Marque / domaine pour l'affichage. */
    domainName: string;
    logo: string;

    /** État de connexion + identité. */
    isLoggedIn = false;
    userId: number | null = null;
    companyId: number | null = null;
    userName = '';

    /** Champs du formulaire. */
    title = '';
    message = '';
    email = '';
    firstname = '';
    lastname = '';

    /** État UI. */
    submitting = false;
    success = false;
    ticketNumber: string | null = null;
    errorMessage = '';
    submitted = false; // true après une tentative de soumission (pour afficher les erreurs)

    /** Limite de l'API sur le titre. */
    readonly titleMaxLength = 500;
    readonly messageMinLength = 20;

    private destroy$ = new Subject<void>();

    constructor(
        private apiService: ApiService,
        private contextService: ContextService,
        private domainService: DomainService,
        private router: Router,
        private messageService: MessageService,
    ) {
        this.domainName = this.domainService.getDomain();
        this.logo = this.domainService.getCurrentCountry() === 'CA' ? 'assets/logo_neon_ca.png' : 'assets/logo.svg';
    }

    ngOnInit(): void {
        this.isLoggedIn = !!localStorage.getItem('access_token');

        if (this.isLoggedIn) {
            // Identité depuis l'observable live, avec fallback localStorage (deep-link direct).
            const storedUser = this.parseJson(localStorage.getItem('currentUser'));
            this.apiService.currentUser$
                .pipe(takeUntil(this.destroy$))
                .subscribe((user) => {
                    const u = user ?? storedUser;
                    this.applyUser(u);
                });

            // Société active depuis le contexte, fallback localStorage.
            this.contextService.context$.pipe(takeUntil(this.destroy$)).subscribe((ctx) => {
                const base = ctx?.base ?? this.parseJson(localStorage.getItem('activeBase'));
                this.companyId = base?.id ?? null;
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private applyUser(user: any): void {
        this.userId = user?.id ?? null;
        if (user) {
            const first = user.firstname || '';
            const last = user.name || '';
            this.userName = [first, last].filter(Boolean).join(' ');
        }
    }

    private parseJson(raw: string | null): any {
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    // ---------- Validation ----------

    get emailValid(): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim());
    }

    get titleValid(): boolean {
        const t = this.title.trim();
        return t.length >= 1 && t.length <= this.titleMaxLength;
    }

    get messageValid(): boolean {
        return this.message.trim().length >= this.messageMinLength;
    }

    get formValid(): boolean {
        if (!this.titleValid || !this.messageValid) return false;
        if (!this.isLoggedIn && !this.emailValid) return false;
        return true;
    }

    get messageCount(): number {
        return this.message.trim().length;
    }

    // ---------- Soumission ----------

    async submit(): Promise<void> {
        this.submitted = true;
        this.errorMessage = '';

        if (!this.formValid) {
            this.messageService.add({
                key: 'help',
                severity: 'warn',
                summary: 'Vérification',
                detail: 'Merci de compléter les champs requis.',
                life: 4000,
            });
            return;
        }

        const apiKey = (window as any).__env?.ticketingKey;
        if (!apiKey) {
            this.errorMessage = "Le service d'assistance n'est pas correctement configuré. Merci de réessayer plus tard.";
            return;
        }

        const payload = this.buildPayload();

        this.submitting = true;
        try {
            const res = await fetch(SAV_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Api-Key': apiKey,
                },
                body: JSON.stringify(payload),
            });

            let data: any = null;
            const text = await res.text();
            try {
                data = text ? JSON.parse(text) : null;
            } catch {
                data = null;
            }

            if (res.ok && data?.success) {
                this.ticketNumber = data.ticket_number ?? null;
                this.success = true;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                this.errorMessage = this.humanizeError(res.status, data);
            }
        } catch (err) {
            // Échec réseau / CORS.
            this.errorMessage = "Impossible de joindre le service d'assistance. Vérifiez votre connexion et réessayez.";
        } finally {
            this.submitting = false;
        }
    }

    private buildPayload(): any {
        const payload: any = {
            title: this.title.trim().slice(0, this.titleMaxLength),
            message: this.message.trim(),
        };

        if (this.isLoggedIn && this.userId) {
            payload.user_id = this.userId;
            if (this.companyId) {
                payload.company_id = this.companyId;
            }
        } else {
            payload.email = this.email.trim();
            if (this.firstname.trim()) payload.firstname = this.firstname.trim();
            if (this.lastname.trim()) payload.lastname = this.lastname.trim();
        }

        return payload;
    }

    private humanizeError(status: number, data: any): string {
        const apiMsg = data?.error;
        if (status === 422) {
            return apiMsg || 'Certains champs requis sont manquants.';
        }
        if (status === 401) {
            return "Le service d'assistance a refusé l'authentification. Merci de réessayer plus tard.";
        }
        return apiMsg || "Une erreur est survenue lors de l'envoi de votre demande. Merci de réessayer.";
    }

    // ---------- Navigation ----------

    goBack(): void {
        // history.back() si on vient de l'app, sinon route par défaut selon l'état de connexion.
        if (window.history.length > 1) {
            window.history.back();
            return;
        }
        this.router.navigate([this.isLoggedIn ? '/chat' : '/login']);
    }

    resetForm(): void {
        this.success = false;
        this.ticketNumber = null;
        this.errorMessage = '';
        this.submitted = false;
        this.title = '';
        this.message = '';
        this.email = '';
        this.firstname = '';
        this.lastname = '';
    }
}
